import Cutout from "./cutout";

export default class Joint {
    readonly cutouts: Cutout[] = [];
    /** Joint Quantity */
    quantity: number;
    /** Length of Odd Legs (Leg 1, Male End) */
    lengthOddLegs: number;
    /** Length of Even Legs (Leg 2) */
    lengthEvenLegs: number;
    /** Duct Type */
    ductType: DuctType;
    /** Bead Type */
    beadType: BeadType;
    /** Damper Location */
    damperLocation: DamperLocation;
    /** Connector 1 Type */
    connector1: ConnectorType;
    /** Connector 2 Type */
    connector2: ConnectorType;
    /** Cleat Location */
    cleatLocation: CleatLocation;
    /** Cleat Type */
    cleatType: CleatType;
    /** Lock Seam Type */
    lockSeam: LockSeamType;
    /** Sealant? */
    sealant: boolean;
    /** Bend Corners? */
    bendCorners: boolean;
    /** Insulation */
    insulation: InsulationThickness;
    /** Max Pin Spacing */
    maxPinSpacing: PinSpacing;
    /** Tie Rods Odd Legs */
    tieRodTypeOddLegs: TieRodType;
    /** Tie Rods Even Legs */
    tieRodTypeEvenLegs: TieRodType;
    /** Tie Rod Odd Legs Row Count */
    tieRodRowsOddLegs: number;
    /** Tie Rod Even Legs Row Count */
    tieRodRowsEvenLegs: number;
    /** Tie Rod Hole Size */
    holeSize: TieRodHoleSize;
    /** Coil Source */
    coilSource: CoilSource;
    /** Coil Gauge value (26 for 26GA) */
    coilGauge: number; // TODO: enum?
    /** Coil Width */
    coilWidth: number;
    /** Reference */
    reference: string;

    constructor(fields: string[]) {
        // make indices 1 based to agree with documentation
        fields.unshift('');
        this.quantity = parseFloat(fields[1]);
        this.lengthOddLegs = parseFloat(fields[2]);
        this.lengthEvenLegs = parseFloat(fields[3]);
        this.ductType = parseInt(fields[4]);
        this.beadType = parseInt(fields[5]);
        this.damperLocation = parseInt(fields[6]);
        this.connector1 = parseInt(fields[7]);
        this.connector2 = parseInt(fields[8]);
        this.cleatLocation = parseInt(fields[9]);
        this.cleatType = parseInt(fields[10]);
        this.lockSeam = parseInt(fields[11]);
        this.sealant = fields[12] === '1';
        this.bendCorners = fields[13] === '1';
        this.insulation = parseInt(fields[14]);
        this.maxPinSpacing = parseInt(fields[15]);
        this.tieRodTypeOddLegs = parseInt(fields[16]);
        this.tieRodTypeEvenLegs = parseInt(fields[17]);
        this.tieRodRowsOddLegs = parseInt(fields[18]);
        this.tieRodRowsEvenLegs = parseInt(fields[19]);
        this.holeSize = parseInt(fields[20]);
        this.coilSource = parseInt(fields[21]);
        this.coilGauge = parseInt(fields[22]);
        this.coilWidth = parseFloat(fields[23]);
        this.reference = fields[24];
    }

    add(cutout: Cutout): Joint {
        this.cutouts.push(cutout);
        return this;
    }

    get enabled() {
        return this.quantity > 0;
    }

    get ductTypeLabel() {
        return DuctTypeLabel.get(this.ductType) || this.ductType.toString();
    }

    get beadTypeLabel() {
        return BeadTypeLabel.get(this.beadType) || this.beadType.toString();
    }

    get damperLocationLabel() {
        return DamperLocationLabel.get(this.damperLocation) || this.damperLocation.toString();
    }

    get c1TypeLabel() {
        return ConnectorTypeLabel.get(this.connector1) || this.connector1.toString();
    }

    get c2TypeLabel() {
        return ConnectorTypeLabel.get(this.connector2) || this.connector2.toString();
    }

    get cleatLocationLabel() {
        return CleatLocationLabel.get(this.cleatLocation) || this.cleatLocation.toString();
    }

    get cleatTypeLabel() {
        return CleatTypeLabel.get(this.cleatType) || this.cleatType.toString();
    }

    get lockSeamTypeLabel() {
        return LockSeamTypeLabel.get(this.lockSeam) || this.lockSeam.toString();
    }

    get insulationThicknessLabel() {
        return InsulationThicknessLabel.get(this.insulation) || this.insulation.toString();
    }

    get pinSpacingLabel() {
        return PinSpacingLabel.get(this.maxPinSpacing) || this.maxPinSpacing.toString();
    }

    get tieRodTypeOddLabel() {
        return TieRodTypeLabel.get(this.tieRodTypeOddLegs) || this.tieRodTypeOddLegs.toString();
    }

    get tieRodTypeEvenLabel() {
        return TieRodTypeLabel.get(this.tieRodTypeEvenLegs) || this.tieRodTypeEvenLegs.toString();
    }

    get tieRodHoldSizeLabel() {
        return TieRodHoleSizeLabel.get(this.holeSize) || this.holeSize.toString();
    }

    get coilSourceLabel() {
        return CoilSourceLabel.get(this.coilSource) || this.coilSource.toString();
    }
}

export enum DuctType {
    Sheets = 0,
    FourSided = 1,
    LShape = 2,
    UShape = 3,
    Wrap = 4,
}

export const DuctTypeLabel = new Map<DuctType, string>([
    [DuctType.Sheets, "Sheets"],
    [DuctType.FourSided, "4-Sided"],
    [DuctType.LShape, "L-Shape"],
    [DuctType.UShape, "U-Shape"],
    [DuctType.Wrap, "Wrap"],
]) as ReadonlyMap<DuctType, string>;

export enum BeadType {
    NoBead = 0,
    FullBead = 1,
    /** No bead at corners and ends */
    SkipBead = 2,
}

export const BeadTypeLabel = new Map<BeadType, string>([
    [BeadType.NoBead, "No Bead"],
    [BeadType.FullBead, "Full Bead"],
    [BeadType.SkipBead, "Skip Bead"],
]) as ReadonlyMap<BeadType, string>;

export enum DamperLocation {
    None = 0,
    OddLegs = 1,
    EvenLegs = 2,
}

export const DamperLocationLabel = new Map<DamperLocation, string>([
    [DamperLocation.None, "None"],
    [DamperLocation.OddLegs, "Odd Legs"],
    [DamperLocation.EvenLegs, "Even Legs"],
]) as ReadonlyMap<DamperLocation, string>;

export enum ConnectorType {
    /** None (Raw) */
    None = 0,
    /** S&D (Slip and Drive) */
    SandD = 1,
    /** TD (Transverse Duct Flange) */
    TD = 2,
    /** Slip-Raw (Large End) */
    SlipRaw = 3,
    /** Slip-Female (Small End) */
    SlipFemale = 4,
}
export const ConnectorTypeLabel = new Map<ConnectorType, string>([
    [ConnectorType.None, "Raw"],
    [ConnectorType.SandD, "S&D"],
    [ConnectorType.TD, "TD"],
    [ConnectorType.SlipRaw, "LE"],
    [ConnectorType.SlipFemale, "SE"],
]) as ReadonlyMap<ConnectorType, string>;

export enum CleatLocation {
    None = 0,
    /** Drive on Odd Legs */
    DriveOnOddLegs = 1,
    /** Drive on Even Legs */
    DriveOnEvenLegs = 2,
    /** Drive on All Legs */
    DriveOnAllLegs = 3,
}

export const CleatLocationLabel = new Map<CleatLocation, string>([
    [CleatLocation.None, "None"],
    [CleatLocation.DriveOnOddLegs, "Drive on Odd Legs"],
    [CleatLocation.DriveOnEvenLegs, "Drive on Even Legs"],
    [CleatLocation.DriveOnAllLegs, "Drive on All Legs"],
]) as ReadonlyMap<CleatLocation, string>;

// This feels obsolete - Can be inferred from Connector 1/Connector 2
export enum CleatType {
    Connector1 = 0,
    Connector2 = 1,
    Both = 2,
}

export const CleatTypeLabel = new Map<CleatType, string>([
    [CleatType.Connector1, "Connector 1"],
    [CleatType.Connector2, "Connector 2"],
    [CleatType.Both, "Both"],
]) as ReadonlyMap<CleatType, string>;

export enum LockSeamType {
    /** Large Pittsburgh */
    LargePittsburgh = 0,
    /** Small Pittsburgh */
    SmallPittsburgh = 1,
    Snap = 2,
    /** Raw 1 - Welded duct with split leg butt seam */
    Raw1 = 3,
    /** Raw 2 - Welded duct with corner butt seam */
    Raw2 = 4,
    /** Male-Male - Not available on shear/brake combo in processor */
    MaleToMale = 5,
    /** Female-Female - not available on shear/brake combo in processor unless female length is greater than or equal to 1/2 the notch width */
    FemaleToFemale = 6,
    /** User-defined 1 */
    User1 = 9,
    /** User-defined 2 */
    User2 = 10,
}
export const LockSeamTypeLabel = new Map<LockSeamType, string>([
    [LockSeamType.LargePittsburgh, "Large Pitts"],
    [LockSeamType.SmallPittsburgh, "Small Pitts"],
    [LockSeamType.Snap, "Snap"],
    [LockSeamType.Raw1, "Raw1"],
    [LockSeamType.Raw2, "Raw2"],
    [LockSeamType.MaleToMale, "MM"],
    [LockSeamType.FemaleToFemale, "FF"],
    [LockSeamType.User1, "User1"],
    [LockSeamType.User2, "User2"],
]) as ReadonlyMap<LockSeamType, string>;

export enum InsulationThickness {
    None = 0,
    /** 0.5" thick */
    In_0_5 = 1,
    /** 1.0" thick */
    In_1_0 = 2,
    /** 1.5" thick */
    In_1_5 = 3,
    /** 2.0" thick */
    In_2_0 = 4,
}

export const InsulationThicknessLabel = new Map<InsulationThickness, string>([
    [InsulationThickness.None, "None"],
    [InsulationThickness.In_0_5, "0.5\""],
    [InsulationThickness.In_1_0, "1.0\""],
    [InsulationThickness.In_1_5, "1.5\""],
    [InsulationThickness.In_2_0, "2.0\""],
]) as ReadonlyMap<InsulationThickness, string>;

export enum PinSpacing {
    None = 0,
    /** 6" */
    In_06 = 1,
    /** 7" */
    In_07 = 2,
    /** 8" */
    In_08 = 3,
    /** 9" */
    In_09 = 4,
    /** 10" */
    In_10 = 5,
    /** 11" */
    In_11 = 6,
    /** 12" */
    In_12 = 7,
}
export const PinSpacingLabel = new Map<PinSpacing, string>([
    [PinSpacing.None, "None"],
    [PinSpacing.In_06, "6\""],
    [PinSpacing.In_07, "7\""],
    [PinSpacing.In_08, "8\""],
    [PinSpacing.In_09, "9\""],
    [PinSpacing.In_10, "10\""],
    [PinSpacing.In_11, "11\""],
    [PinSpacing.In_12, "12\""],
]) as ReadonlyMap<PinSpacing, string>;

export enum TieRodType {
    None = 0,
    /** Left - Connector 1 end */
    Left = 1,
    /** Center */
    Center = 2,
    /** Right - Connector 2 end */
    Right = 3,
    /** L&C - Connector 1 end and center */
    LeftAndCenter = 4,
    /** R&C - Connector 2 end and center */
    RightAndCenter = 5,
    /** L&R - Both ends */
    LeftAndRight = 6,
    /** All - Both ends and center */
    All = 7,
    /** Diamond - center and then both ends and then center */
    Diamond = 8,
}

export const TieRodTypeLabel = new Map<TieRodType, string>([
    [TieRodType.None, "None"],
    [TieRodType.Left, "Left"],
    [TieRodType.Center, "Center"],
    [TieRodType.Right, "Right"],
    [TieRodType.LeftAndCenter, "Left and Center"],
    [TieRodType.RightAndCenter, "Right and Center"],
    [TieRodType.LeftAndRight, "Left and Right"],
    [TieRodType.All, "Both Ends and Center"],
    [TieRodType.Diamond, "Diamon"],
]) as ReadonlyMap<TieRodType, string>;

export enum TieRodHoleSize {
    /** 5/16" */
    In_5_16 = 0,
    /** 7/16" */
    In_7_16 = 1,
}

export const TieRodHoleSizeLabel = new Map<TieRodHoleSize, string>([
    [TieRodHoleSize.In_5_16, "5/16\""],
    [TieRodHoleSize.In_7_16, "7/16\""],
]) as ReadonlyMap<TieRodHoleSize, string>;

export enum CoilSource {
    Expandrel = 0,
    /** Coil Station 1 */
    CoilStation01 = 1,
    /** Coil Station 2 */
    CoilStation02 = 2,
    /** Coil Station 3 */
    CoilStation03 = 3,
    /** Coil Station 4 */
    CoilStation04 = 4,
    /** Coil Station 5 */
    CoilStation05 = 5,
    /** Coil Station 6 */
    CoilStation06 = 6,
    /** Coil Station 7 */
    CoilStation07 = 7,
    /** Coil Station 8 */
    CoilStation08 = 8,
    /** Coil Station 9 */
    CoilStation09 = 9,
    /** Coil Station 10 */
    CoilStation10 = 10,
}

export const CoilSourceLabel = new Map<CoilSource, string>([
    [CoilSource.Expandrel, "Expendrel"],
    [CoilSource.CoilStation01, "Coil Station 1"],
    [CoilSource.CoilStation02, "Coil Station 2"],
    [CoilSource.CoilStation03, "Coil Station 3"],
    [CoilSource.CoilStation04, "Coil Station 4"],
    [CoilSource.CoilStation05, "Coil Station 5"],
    [CoilSource.CoilStation06, "Coil Station 6"],
    [CoilSource.CoilStation07, "Coil Station 7"],
    [CoilSource.CoilStation08, "Coil Station 8"],
    [CoilSource.CoilStation09, "Coil Station 9"],
    [CoilSource.CoilStation10, "Coil Station 10"],
]) as ReadonlyMap<CoilSource, string>;
